import React, { useState, useEffect } from "react"
import AOS from 'aos'
import 'aos/dist/aos.css'

// Add this utility function at the top of the file
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    // Initial AOS setup based on device
    if (window.innerWidth <= 768) {
      // Remove AOS for mobile
      document.querySelectorAll('[data-aos]').forEach(elem => {
        elem.removeAttribute('data-aos')
      })
    } else {
      // Initialize AOS for desktop
      AOS.init({
        duration: 1500,
        once: false,
        mirror: true
      })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return isMobile
}

// Add this function to handle file conversion
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = error => reject(error);
  });
};

export const QuantumComputingPage = ({ isDarkMode }) => {
  const [expandedIndustry, setExpandedIndustry] = useState(null)
  const isMobile = useIsMobile()

  // Remove data-aos attributes if it's mobile
  const getAOSProps = (animation) => {
    return isMobile ? {} : { "data-aos": animation }
  }

  const industries = [
    {
      id: "quantum-ai-ml",
      title: "Quantum AI & ML",
      description: "Advanced machine learning powered by quantum computing",
      details: [
        "Quantum-enhanced neural networks",
        "Accelerated model training and optimization",
        "Hybrid quantum-classical ML algorithms",
      ],
    },
    {
      id: "healthcare",
      title: "Healthcare",
      description: "Drug discovery and molecular simulations",
      details: ["Personalized medicine development", "Protein structure analysis", "Advanced genomic studies"],
    },
    {
      id: "finance",
      title: "Finance",
      description: "Risk analysis and portfolio optimization",
      details: [
        "High-frequency trading optimization",
        "Fraud detection using quantum algorithms",
        "Portfolio risk assessments",
      ],
    },
    {
      id: "energy",
      title: "Energy",
      description: "Optimization of power grids and resource allocation",
      details: [
        "Renewable energy distribution optimization",
        "Power grid stability analysis",
        "Energy resource forecasting",
      ],
    },
    {
      id: "cybersecurity",
      title: "Cybersecurity",
      description: "Quantum-enhanced security solutions and cryptography",
      details: [
        "Development of quantum-safe encryption algorithms",
        "Detection of cyber threats using quantum algorithms",
        "Secure quantum key distribution (QKD)",
      ],
    },
  ]

  const toggleExpand = (id) => {
    setExpandedIndustry((prev) => (prev === id ? null : id))
  }

  return (
    <div className={`quantum-computing-page ${isDarkMode ? "dark" : ""}`}>
      {/* Content Wrapper */}
      <div className="content-wrapper">
        {/* Left Content */}
        <div className="text-content" {...getAOSProps("fade-right")}>
          <h1 className="page-title">Quantum Computing Solutions</h1>
          <p className="page-description" {...getAOSProps("fade-up")}>
            Advanced quantum algorithms tailored for your computational needs. We specialize in developing cutting-edge
            quantum solutions to tackle the most complex challenges across various industries.
          </p>
          {/* Key Features Section */}
          <section className="details" {...getAOSProps("fade-up")}>
            <h2 className="section-title">Key Features</h2>
            <ul className="features-list">
              <li>Optimization of complex problems using quantum algorithms</li>
              <li>Development of quantum-based cryptography for enhanced security</li>
              <li>Simulation of quantum systems for research and development</li>
              <li>Integration of hybrid quantum-classical workflows</li>
            </ul>
          </section>
        </div>
        {/* Image Section */}
        <div className="image-container" {...getAOSProps("fade-left")}>
          <img src="/assets/images/Your paragraph text (5).png" alt="Quantum Computer" className="quantum-image" />
        </div>
      </div>
      {/* Industries Section */}
      <section className="industries">
        <h2 className="section-title" {...getAOSProps("fade-up")}>Industries We Serve</h2>
        <div className="industries-grid" {...getAOSProps("fade-up")}>
          {industries.map((industry) => (
            <div className="industry-card" key={industry.id}>
              <h3>{industry.title}</h3>
              <p>{industry.description}</p>
              <button onClick={() => toggleExpand(industry.id)} className="dropdown-button">
                {expandedIndustry === industry.id ? "Show Less" : "Show More"}
              </button>
              {expandedIndustry === industry.id && (
                <ul className="dropdown-content">
                  {industry.details.map((detail, index) => (
                    <li key={index}>{detail}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>
      {/* Future Section */}
      <section className="future" {...getAOSProps("fade-up")}>
        <h2 className="section-title">The Future of Quantum Computing</h2>
        <p>
          At Revren QAI, we are committed to leading the quantum revolution by providing scalable, innovative solutions
          that harness the true power of quantum computing.
        </p>
        <div className="commitment-section" {...getAOSProps("fade-up")}>
      <h3>Our Commitment</h3>
      <p>
        We are dedicated to making quantum computing accessible, practical, and
        transformative for businesses across all sectors. Through continuous
        innovation and research, we're building the foundation for a quantum-powered
        future.
      </p>
    </div>
      </section>
    </div>
  )
}

export const AIIntegrationPage = ({ isDarkMode }) => {
  const isMobile = useIsMobile()
  const getAOSProps = (animation) => {
    return isMobile ? {} : { "data-aos": animation }
  }

  return (
    <div className={`ai-integration-page ${isDarkMode ? "dark" : ""}`}>
      <div className="content-wrapper">
        <div className="text-content" {...getAOSProps("fade-up")}>
          <h1 className="page-title">AI Integration Solutions</h1>
          <p className="page-description" {...getAOSProps("fade-up")}>
          Advanced quantum algorithms tailored for your computational needs. We specialize in developing cutting-edge
          quantum solutions to tackle the most complex challenges across various industries.
          </p>
          <section className="details">
            <h2 className="section-title" {...getAOSProps("fade-up")}>Key Features</h2>
            <ul className="features-list" {...getAOSProps("fade-up")}>
            <li>Optimization of complex problems using quantum algorithms</li>
              <li>Development of quantum-based cryptography for enhanced security</li>
              <li>Simulation of quantum systems for research and development</li>
              <li>Integration of hybrid quantum-classical workflows</li>
              <li>Quantum-enhanced AI and machine learning models</li>
              <li>Solving combinatorial optimization problems at unprecedented speeds</li>
            </ul>
          </section>
        </div>
        <div className="image-container" {...getAOSProps("fade-up")}>
          <img src="/assets/images/ai-int.jpg" alt="AI Integration" className="ai-image" />
        </div>
      </div>
      <section className="applications">
        <h2 className="section-title" {...getAOSProps("fade-up")}>Applications</h2>
        <div className="applications-grid">
          <div className="application-card" {...getAOSProps("fade-right")}>
            <h3>Natural Language Processing</h3>
            <p>Enhanced language understanding and generation</p>
          </div>
          <div className="application-card" {...getAOSProps("fade-right")}>
            <h3>Computer Vision</h3>
            <p>Advanced image and video analysis capabilities</p>
          </div>
          <div className="application-card" {...getAOSProps("fade-left")}>
            <h3>Predictive Analytics</h3>
            <p>Improved forecasting and trend analysis</p>
          </div>
          <div className="application-card" {...getAOSProps("fade-left")}>
            <h3>Autonomous Systems</h3>
            <p>Optimized decision-making for robotics and self-driving vehicles</p>
          </div>
        </div>
      </section>
      <section className="benefits" {...getAOSProps("fade-up")}>
        <h2 className="section-title">Benefits of AI Integration</h2>
        <p>
          By integrating our quantum-enhanced AI solutions, businesses can achieve unprecedented levels of efficiency,
          accuracy, and innovation. Our AI integration services pave the way for smarter decision-making, automation of
          complex tasks, and the discovery of insights that were previously out of reach.
        </p>
      </section>
      
    </div>
  )
}

export const QuantumMLPage = ({ isDarkMode }) => {
  const isMobile = useIsMobile()
  const getAOSProps = (animation) => {
    return isMobile ? {} : { "data-aos": animation }
  }

  return (
    <div className={`quantum-ml-page ${isDarkMode ? "dark" : ""}`}>
      <div className="content-wrapper">
        <div className="text-content" {...getAOSProps("fade-right")}>
          <h1 className="page-title">Quantum Machine Learning</h1>
          <p className="page-description" {...getAOSProps("fade-up")}>
            Unlock the potential of next-generation machine learning algorithms powered by quantum systems. Revolutionize
            AI applications with quantum-enhanced techniques.
          </p>
          <section className="details" {...getAOSProps("fade-up")}>
            <h2 className="section-title">Key Features</h2>
            <ul className="features-list">
              <li>Quantum-enhanced data processing and feature extraction</li>
              <li>Development of hybrid quantum-classical ML models</li>
              <li>Accelerated training of machine learning algorithms</li>
              <li>Quantum kernels for improved classification and clustering</li>
            </ul>
          </section>
        </div>
        <div className="image-container" {...getAOSProps("fade-left")}>
          <img src="/assets/images/QUA-MAC.jpg" alt="Quantum Machine Learning" className="quantum-ml-image" />
        </div>
      </div>
      <section className="applications">
        <h2 className="section-title" {...getAOSProps("fade-up")}>Applications</h2>
        <div className="applications-grid">
          <div className="application-card" {...getAOSProps("fade-right")}>
            <h3>Personalized Recommendations</h3>
            <p>Enhancing recommendation systems using quantum-powered insights</p>
          </div>
          <div className="application-card" {...getAOSProps("fade-right")}>
            <h3>Fraud Detection</h3>
            <p>Quantum ML models for real-time anomaly detection</p>
          </div>
          <div className="application-card" {...getAOSProps("fade-left")}>
            <h3>Image Processing</h3>
            <p>Faster and more accurate image analysis</p>
          </div>
          <div className="application-card" {...getAOSProps("fade-left")}>
            <h3>Genomics</h3>
            <p>Quantum-enabled analysis of genetic data for research</p>
          </div>
        </div>
      </section>
      <section className="benefits" {...getAOSProps("fade-up")}>
        <h2 className="section-title">Benefits</h2>
        <p>
          Quantum Machine Learning empowers businesses with unparalleled computational power, enabling faster data
          processing, more accurate predictions, and the ability to solve complex problems that were previously
          intractable.
        </p>
      </section>
    </div>
  )
}

export const GenerativeAIPage = ({ isDarkMode }) => {
  const isMobile = useIsMobile()
  const getAOSProps = (animation) => {
    return isMobile ? {} : { "data-aos": animation }
  }

  return (
    <div className={`generative-ai-page ${isDarkMode ? "dark" : ""}`}>
      <div className="content-wrapper">
        <div className="text-content" {...getAOSProps("fade-right")}>
          <h1 className="page-title">Generative AI</h1>
          <p className="page-description" {...getAOSProps("fade-up")}>
            Harness the power of advanced AI systems to generate innovative content, designs, and creative solutions
            across various domains.
          </p>
          <section className="details" {...getAOSProps("fade-up")}>
            <h2 className="section-title">Key Features</h2>
            <ul className="features-list">
              <li>AI-driven content generation for marketing and media</li>
              <li>Creation of realistic virtual environments and characters</li>
              <li>Generative design for engineering and architecture</li>
              <li>Custom AI models tailored to your industry needs</li>
            </ul>
          </section>
        </div>
        <div className="image-container" {...getAOSProps("fade-left")}>
          <img src="/assets/images/GEN-AI.jpg" alt="Generative AI" className="generative-ai-image" />
        </div>
      </div>
      <section className="applications">
        <h2 className="section-title" {...getAOSProps("fade-up")}>Applications</h2>
        <div className="applications-grid">
          <div className="application-card" {...getAOSProps("fade-right")}>
            <h3>Marketing</h3>
            <p>Generate personalized ads and campaigns</p>
          </div>
          <div className="application-card" {...getAOSProps("fade-right")}>
            <h3>Design</h3>
            <p>AI-assisted designs for products and prototypes</p>
          </div>
          <div className="application-card" {...getAOSProps("fade-left")}>
            <h3>Content Creation</h3>
            <p>Automated generation of text, images, and videos</p>
          </div>
          <div className="application-card" {...getAOSProps("fade-left")}>
            <h3>Entertainment</h3>
            <p>Realistic character generation for gaming and films</p>
          </div>
        </div>
      </section>
      <section className="benefits" {...getAOSProps("fade-up")}>
        <h2 className="section-title">Benefits</h2>
        <p>
          Generative AI empowers businesses with unparalleled creativity, enabling faster product development, automated
          content generation, and enhanced customer engagement.
        </p>
      </section>
      <com />
    </div>
  )
}

export const PredictiveAnalyticsPage = ({ isDarkMode }) => {
  const isMobile = useIsMobile()
  const getAOSProps = (animation) => {
    return isMobile ? {} : { "data-aos": animation }
  }

  return (
    <div className={`predictive-analytics-page ${isDarkMode ? "dark" : ""}`}>
      <div className="content-wrapper">
        <div className="text-content"{...getAOSProps("fade-right")}>
          <h1 className="page-title">Quantum Predictive Analytics</h1>
          <p className="page-description"{...getAOSProps("fade-up")}>
            Leverage the power of predictive modeling and quantum-enhanced data analysis to drive smarter decisions,
            optimize operations, and gain actionable insights for your business.
          </p>
          <section className="details"{...getAOSProps("fade-up")}>
            <h2 className="section-title">Key Features</h2>
            <ul className="features-list">
              <li>Time series analysis for accurate forecasting</li>
              <li>Quantum-driven big data processing</li>
              <li>Enhanced pattern recognition and clustering</li>
              <li>Predictive modeling using AI and quantum techniques</li>
              <li>Customizable dashboards for actionable insights</li>
              <li>Real-time data integration and monitoring</li>
            </ul>
          </section>
        </div>
        <div className="image-container"{...getAOSProps("fade-left")}>
          <img src="/assets/images/QUA-PRED.jpg" alt="Predictive Analytics" className="predictive-analytics-image" />
        </div>
      </div>
      <section className="applications">
        <h2 className="section-title"{...getAOSProps("fade-up")}>Applications</h2>
        <div className="applications-grid">
          <div className="application-card"{...getAOSProps("fade-right")}>
            <h3>Financial Forecasting</h3>
            <p>Predict market trends and investment opportunities</p>
          </div>
          <div className="application-card"{...getAOSProps("fade-right")}>
            <h3>Supply Chain</h3>
            <p>Optimize inventory and resource allocation</p>
          </div>
          <div className="application-card"{...getAOSProps("fade-left")}>
            <h3>Healthcare</h3>
            <p>Analyze patient data for disease prevention</p>
          </div>
          <div className="application-card"{...getAOSProps("fade-left")}>
            <h3>Marketing</h3>
            <p>Predict consumer behavior for targeted campaigns</p>
          </div>
        </div>
      </section>
      <section className="benefits"{...getAOSProps("fade-up")}>
        <h2 className="section-title">Benefits</h2>
        <p>
          Predictive and Quantum Analytics empower businesses with unparalleled speed, accuracy, and efficiency. Gain
          insights into complex data, reduce uncertainty, and make smarter strategic decisions with data-driven solutions.
        </p>
      </section>
    </div>
  )
}

export const QuantumCloudServicePage = ({ isDarkMode }) => {
  const isMobile = useIsMobile()
  const getAOSProps = (animation) => {
    return isMobile ? {} : { "data-aos": animation }
  }

  return (
    <div className={`quantum-cloud-service-page ${isDarkMode ? "dark" : ""}`}>
      <div className="content-wrapper">
        <div className="text-content"{...getAOSProps("fade-right")}>
          <h1 className="page-title">Quantum Cloud Service</h1>
          <p className="page-description"{...getAOSProps("fade-up")}>
            Access cutting-edge quantum computing power through our cloud-based platform. Harness the potential of quantum
            algorithms without the need for in-house quantum hardware.
          </p>
          <section className="details"{...getAOSProps("fade-up")}>
            <h2 className="section-title">Key Features</h2>
            <ul className="features-list">
              <li>On-demand access to quantum computing resources</li>
              <li>User-friendly interface for quantum algorithm development</li>
              <li>Integration with classical cloud computing services</li>
              <li>Scalable quantum processing units (QPUs)</li>
              <li>Comprehensive suite of quantum software development tools</li>
            </ul>
          </section>
        </div>
        <div className="image-container"{...getAOSProps("fade-left")}>
          <img src="/assets/images/QUA-CLO.jpg" alt="Quantum Cloud Service" className="quantum-cloud-image" />
        </div>
      </div>
      <section className="applications">
        <h2 className="section-title"{...getAOSProps("fade-up")}>Applications</h2>
        <div className="applications-grid">
          <div className="application-card"{...getAOSProps("fade-right")}>
            <h3>Cryptography</h3>
            <p>Develop and test quantum-resistant encryption methods</p>
          </div>
          <div className="application-card"{...getAOSProps("fade-right")}>
            <h3>Financial Modeling</h3>
            <p>Optimize investment strategies and risk assessment</p>
          </div>
          <div className="application-card"{...getAOSProps("fade-left")}>
            <h3>Drug Discovery</h3>
            <p>Accelerate molecular simulations for pharmaceutical research</p>
          </div>
          <div className="application-card"{...getAOSProps("fade-left")}>
            <h3>Machine Learning</h3>
            <p>Enhance AI models with quantum-inspired algorithms</p>
          </div>
        </div>
      </section>
      <section className="benefits"{...getAOSProps("fade-up")}>
        <h2 className="section-title">Benefits</h2>
        <p>
          Our Quantum Cloud Service democratizes access to quantum computing, enabling businesses of all sizes to leverage
          this groundbreaking technology. Experience the power of quantum computing without the need for significant
          hardware investments or specialized expertise.
        </p>
      </section>
    </div>
  )
}

export const ProductsPage = ({ isDarkMode }) => {
  const isMobile = useIsMobile()
  const getAOSProps = (animation) => {
    return isMobile ? {} : { "data-aos": animation }
  }

  const [expandedCategory, setExpandedCategory] = useState(null)

  const productCategories = [
    {
      id: "enterprise-solutions",
      title: "Enterprise Solutions",
      description: "Scalable quantum computing platforms for large organizations",
      details: [
        "Customized quantum computing hardware integration",
        "Enterprise-grade security and infrastructure",
        "Optimization of large-scale business processes",
      ],
    },
    {
      id: "developer-tools",
      title: "Developer Tools",
      description: "Comprehensive SDK and API solutions",
      details: [
        "SDKs and APIs for creating and testing quantum programs.",
        "Support for popular coding languages.",
        "Easy integration of quantum and classical computing.",
      ],
    },
    {
      id: "cloud-services",
      title: "Cloud Services",
      description: "On-demand quantum computing resources",
      details: [
        "On-demand quantum computing power via the cloud.",
        "No need for expensive hardware.",
        "Flexible, pay-as-you-go pricing.",
      ],
    },
    {
      id: "industry-specific-tools",
      title: "Industry-Specific Tools",
      description: "Tailored solutions for various sectors",
      details: [
        "Quantum solutions for finance and healthcare",
        "Solve challenges like optimization and drug discovery.",
        "Built to address industry-specific problems.",
      ],
    },
  ]

  const toggleExpand = (id) => {
    setExpandedCategory((prev) => (prev === id ? null : id))
  }

  return (
    <div className={`quantum-computing-page ${isDarkMode ? "dark" : ""}`}>
      <div className="content-wrapper">
        <div className="text-content" {...getAOSProps("fade-right")}>
          <h1 className="page-title">Our Products</h1>
          <p className="page-description">
            Discover our innovative quantum and AI-powered solutions designed to transform your business operations.
          </p>
          <section className="details">
            <h2 className="section-title">Featured Projects</h2>
            <ul className="features-list">
              <li>gen-ai - Advanced AI systems for content creation and automation</li>
              <li>predictive-analytics - Data-driven forecasting and business intelligence</li>
              <li>QuantumML Suite™ - Quantum-enhanced machine learning toolkit</li>
              <li>CloudQ™ - Quantum cloud computing solution</li>
            </ul>
          </section>
        </div>
        <div className="image-container" {...getAOSProps("fade-left")}>
          <img src="/assets/images/prod.PNG" alt="Products Overview" className="quantum-image" />
        </div>
      </div>
      <section className="applications">
        <h2 className="section-title" {...getAOSProps("fade-up")}>Product Categories</h2>
        <div className="applications-grid">
          {productCategories.map((category, index) => (
            <div 
              className="application-card" 
              key={category.id}
              {...getAOSProps(index % 2 === 0 ? "fade-right" : "fade-left")}
            >
              <h3>{category.title}</h3>
              <p>{category.description}</p>
              <button onClick={() => toggleExpand(category.id)} className="dropdown-button">
                {expandedCategory === category.id ? "Show Less" : "Show More"}
              </button>
              {expandedCategory === category.id && (
                <ul className="dropdown-content">
                  {category.details.map((detail, index) => (
                    <li key={index}>{detail}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}

export const CareersPage = ({ isDarkMode }) => {
  const isMobile = useIsMobile();
  const getAOSProps = (animation) => {
    return isMobile ? {} : { "data-aos": animation }
  };

  const [selectedJob, setSelectedJob] = useState(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false); // Add this line

  const handleApplyClick = (jobTitle) => {
    setSelectedJob(jobTitle);
    setShowApplicationModal(true); // Add this line
  };

  const handleFormClose = () => {
    setSelectedJob(null);
    setShowApplicationModal(false); // Add this line
  };

  // Update the handleSubmit function
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted');

    try {
      const fileInput = e.target.resume;
      let resumeData = null;

      if (fileInput.files.length > 0) {
        const file = fileInput.files[0];
        resumeData = {
          name: file.name,
          data: await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.readAsDataURL(file);
          })
        };
      }

      const formData = {
        name: e.target.name.value,
        email: e.target.email.value,
        phone: e.target.phone.value,
        position: selectedJob,
        message: e.target.message.value,
        resume: resumeData
      };

      const response = await fetch('http://localhost:5001/api/apply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (data.success) {
        alert('Application submitted successfully!');
        e.target.reset();
        setShowApplicationModal(false);
        setSelectedJob(null);
      } else {
        throw new Error(data.message || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to submit application. Please try again.');
    }
  };

  return (
    <div className={`careers-page ${isDarkMode ? "dark" : ""}`}>
      <div className="content-wrapper">
        <div className="text-content" {...getAOSProps("fade-right")}>
          <h1 className="page-title">Careers at Revren QAI</h1>
          <p className="page-description">
            Join our innovative team and help shape the future of quantum computing and artificial intelligence.
          </p>
          <section className="details">
            <h2 className="section-title">Why Join Us?</h2>
            <ul className="features-list">
              <li>Innovation - Work on cutting-edge quantum and AI technologies</li>
              <li>Growth - Continuous learning and development opportunities</li>
              <li>Benefits - Competitive compensation and comprehensive benefits package</li>
              <li>Culture - Collaborative and inclusive work environment</li>
            </ul>
          </section>
        </div>
        <div className="image-container" {...getAOSProps("fade-left")}>
          <img src="/assets/images/CARRI.jpg" alt="Careers at Revren" className="quantum-image" />
        </div>
      </div>
      <section className="applications">
        <h2 className="section-title" {...getAOSProps("fade-up")}>Current Openings</h2>
        <div className="applications-grid">
          {[
            {
              title: "Senior Quantum Algorithm Engineer",
              description: "Design and implement quantum computing solutions",
            },
            {
              title: "AI Research Scientist",
              description: "Lead research in quantum-enhanced AI",
            },
            {
              title: "Full Stack Developer",
              description: "Build next-gen quantum computing interfaces",
            },
            {
              title: "Product Manager",
              description: "Shape the future of quantum products",
            },
          ].map((job, index) => (
            <div 
              className="application-card" 
              key={job.title}
              {...getAOSProps(index % 2 === 0 ? "fade-right" : "fade-left")}
            >
              <h3>{job.title}</h3>
              <p>{job.description}</p>
              <button className="apply-button" onClick={() => handleApplyClick(job.title)}>
                Apply
              </button>
            </div>
          ))}
        </div>
      </section>

      {selectedJob && (
        <div className="application-form-modal">
          <div className="form-container">
            <h2>Apply for {selectedJob}</h2>
            <form onSubmit={handleSubmit} className="application-form">
              <input type="hidden" name="position" value={selectedJob} />
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input type="text" id="name" name="name" className="input" required />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input type="email" id="email" name="email" className="input" required />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input 
                  type="tel" 
                  id="phone" 
                  name="phone" 
                  className="input" 
                  pattern="[0-9]{10}"
                  placeholder="Enter 10-digit number"
                  required 
                />
              </div>
              <div className="form-group">
                <label htmlFor="resume">Upload Resume</label>
                <input 
                  type="file" 
                  id="resume" 
                  name="resume" 
                  accept=".pdf,.doc,.docx" 
                  className="input" 
                  required 
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">Cover Letter</label>
                <textarea 
                  id="message" 
                  name="message" 
                  className="textarea" 
                  required
                ></textarea>
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-button">
                  Submit Application
                </button>
                <button type="button" className="cancel-button" onClick={handleFormClose}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

